import React, { useState, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
    IconPlus, IconArchive, IconEye,
    IconLayoutList, IconBuildingSkyscraper, IconSearch, IconX,
} from '@tabler/icons-react'
import { getSwmsStore } from 'services/swmsStore.svc'
import { getProjectSites } from 'services/clients.svc'
import { getSwmsAssignments, createSwmsAssignment, archiveSwmsAssignment, getAssignmentPdfUrl } from 'services/swmsAssignment.svc'
import { TRADES } from '@paramount-prestart/shared/tradesEnum'
import ConfirmModal from './ConfirmModal'

// ── Helpers ───────────────────────────────────────────────────────────────────

const ASSIGNABLE_TRADES = TRADES.filter((t) => t.value !== 'other')

function tradeLabel(value) {
    return TRADES.find((t) => t.value === value)?.label ?? value
}

// ── Status badge ──────────────────────────────────────────────────────────────

function AssignBadge({ status }) {
    const cls = status === 'active' ? 'sa-badge-active' : 'sa-badge-archived'
    return <span className={`sa-badge ${cls}`}>{status === 'active' ? 'Active' : 'Archived'}</span>
}

// ── Site badges ───────────────────────────────────────────────────────────────

function SiteBadge({ site }) {
    const cls = site?.status === 'complete' ? 'sa-site-badge-complete' : 'sa-site-badge-active'
    const label = site?.fullAddress || site?.address || 'Unknown site'
    return <span className={`sa-site-badge ${cls}`}>{label}</span>
}

// ── Trade chips (display only) ────────────────────────────────────────────────

function TradePills({ trades }) {
    return (
        <div className="sa-trade-pills">
            {trades.map((t) => (
                <span key={t} className="sa-trade-pill">{tradeLabel(t)}</span>
            ))}
        </div>
    )
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ message, show, isError }) {
    return (
        <div className={`sm-toast${show ? ' show' : ''}${isError ? ' sm-toast-error' : ''}`}>
            {message}
        </div>
    )
}

// ── SWMS viewer overlay ───────────────────────────────────────────────────────

function ViewerOverlay({ record, onClose }) {
    const { data: pdfUrl, isError: pdfError } = useQuery(
        ['assignmentPdfUrl', record?._id],
        () => getAssignmentPdfUrl(record._id, 'signed').then((res) => res.url),
        { enabled: !!record?._id, staleTime: 5 * 60 * 1000 }
    )

    if (!record) return null
    const swms = record.swmsStore
    return (
        <div className="sm-view-overlay open" onClick={onClose}>
            <div className="sm-view-shell" onClick={(e) => e.stopPropagation()}>
                <div className="sm-view-toolbar">
                    <div className="sm-view-toolbar-left">
                        <span className="sm-view-title">{swms?.title ?? 'SWMS'}</span>
                        <span className={`sm-view-badge${record.status === 'archived' ? ' vt-arch' : ' vt-pub'}`}>
                            {record.status === 'archived' ? 'Archived' : 'Active'}
                        </span>
                    </div>
                    <button className="sm-vt-btn" onClick={onClose}>
                        <IconX size={14} stroke={1.5} /> Close
                    </button>
                </div>
                <div className="sm-view-scroll">
                    {pdfError && (
                        <div className="sa-pdf-placeholder">
                            PDF not available yet.
                        </div>
                    )}
                    {!pdfError && !pdfUrl && (
                        <div className="sa-pdf-placeholder">
                            Loading PDF…
                        </div>
                    )}
                    {pdfUrl && (
                        <iframe
                            src={pdfUrl}
                            title="SWMS View"
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Confirm modal ─────────────────────────────────────────────────────────────

// ── Assign modal ──────────────────────────────────────────────────────────────

function AssignModal({ open, swmsList, sites, onClose, onSubmit, isSubmitting }) {
    const [swmsId, setSwmsId] = useState('')
    const [selectedSiteIds, setSelectedSiteIds] = useState([])
    const [selectedTrades, setSelectedTrades] = useState([])
    const [siteSearch, setSiteSearch] = useState('')

    const toggleTrade = (value) =>
        setSelectedTrades((prev) =>
            prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
        )

    const toggleSite = (id) =>
        setSelectedSiteIds((prev) =>
            prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
        )

    const filteredSites = useMemo(() => {
        const q = siteSearch.toLowerCase().trim()
        if (!q) return sites
        return sites.filter((s) =>
            (s.fullAddress || s.address || s.siteNumber || '')
                .toLowerCase()
                .includes(q)
        )
    }, [sites, siteSearch])

    const canSubmit = swmsId && selectedSiteIds.length > 0 && selectedTrades.length > 0

    const handleSubmit = () => {
        if (!canSubmit) return
        // Create one assignment per site
        selectedSiteIds.forEach((siteId) => {
            onSubmit({ swmsStore: swmsId, site: siteId, trades: selectedTrades })
        })
    }

    const handleClose = () => {
        setSwmsId('')
        setSelectedSiteIds([])
        setSelectedTrades([])
        setSiteSearch('')
        onClose()
    }

    if (!open) return null

    return (
        <div className="sm-confirm-overlay open" onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose() }}>
            <div className="sa-modal sa-modal-wide" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                    <h3>Assign SWMS to Sites</h3>
                    <button className="sa-modal-close" onClick={handleClose}><i className="ti ti-x" /></button>
                </div>

                {/* SWMS selector */}
                <div className="sa-modal-field">
                    <label>SWMS <span className="sa-modal-req">*</span></label>
                    <select value={swmsId} onChange={(e) => setSwmsId(e.target.value)}>
                        <option value="">Select a published SWMS…</option>
                        {swmsList.map((s) => (
                            <option key={s._id} value={s._id}>{s.title}</option>
                        ))}
                    </select>
                </div>

                {/* Trades */}
                <div className="sa-modal-field">
                    <label>Trades <span className="sa-modal-req">*</span></label>
                    <div className="sa-trade-chip-wrap">
                        {ASSIGNABLE_TRADES.map((t) => (
                            <button
                                key={t.value}
                                className={`sa-trade-chip-toggle${selectedTrades.includes(t.value) ? ' on' : ''}`}
                                onClick={() => toggleTrade(t.value)}
                                type="button"
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Site picker */}
                <div className="sa-modal-field">
                    <div className="sa-site-picker-header">
                        <label>
                            Sites <span className="sa-modal-req">*</span>
                        </label>
                        <div className="sa-site-search-wrap">
                            <i className="ti ti-search" />
                            <input
                                type="text"
                                placeholder="Search sites…"
                                value={siteSearch}
                                onChange={(e) => setSiteSearch(e.target.value)}
                            />
                            {siteSearch && (
                                <button onClick={() => setSiteSearch('')}><i className="ti ti-x" /></button>
                            )}
                        </div>
                    </div>
                    <div className="sa-site-grid">
                        {filteredSites.length === 0 && (
                            <p className="sa-site-empty">No sites match your search.</p>
                        )}
                        {filteredSites.map((s) => {
                            const selected = selectedSiteIds.includes(s._id)
                            const addr = s.fullAddress || s.address || s.siteNumber || '—'
                            const isComplete = s.status === 'complete'
                            return (
                                <button
                                    key={s._id}
                                    type="button"
                                    className={`sa-site-card${selected ? ' selected' : ''}${isComplete ? ' complete' : ''}`}
                                    onClick={() => toggleSite(s._id)}
                                >
                                    <div className="sa-site-card-check">
                                        {selected && <i className="ti ti-check" />}
                                    </div>
                                    <div className="sa-site-card-body">
                                        <span className="sa-site-card-addr">{addr}</span>
                                        {s.clientName && <span className="sa-site-card-client">{s.clientName}</span>}
                                        {isComplete && <span className="sa-site-card-status">Completed</span>}
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                </div>

                <div className="sm-confirm-actions">
                    <button className="sm-confirm-cancel" onClick={handleClose}>Cancel</button>
                    <button
                        className="sm-confirm-ok publish"
                        onClick={handleSubmit}
                        disabled={!canSubmit || isSubmitting}
                        title={!swmsId ? 'Select a SWMS first' : !selectedTrades.length ? 'Select at least one trade' : !selectedSiteIds.length ? 'Select at least one site' : ''}
                    >
                        {isSubmitting
                            ? 'Assigning…'
                            : selectedSiteIds.length > 0
                                ? `Assign to ${selectedSiteIds.length} site${selectedSiteIds.length !== 1 ? 's' : ''}`
                                : 'Assign'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── SWMS-view table ───────────────────────────────────────────────────────────

function SwmsViewTable({ rows, onArchive, onView }) {
    if (rows.length === 0) {
        return <p className="sm-empty sa-empty-padded">No assignments found.</p>
    }
    return (
        <table className="sa-table">
            <thead>
                <tr>
                    <th className="sa-col-swms">SWMS</th>
                    <th className="sa-col-sites">Sites</th>
                    <th className="sa-col-trades">Trades</th>
                    <th className="sa-col-status">Status</th>
                    <th className="sa-col-actions"></th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row._id} className={row.status === 'archived' ? 'sa-row-archived' : ''}>
                        <td className="sa-cell-primary">{row.swmsStore?.title ?? '—'}</td>
                        <td><SiteBadge site={row.site} /></td>
                        <td><TradePills trades={row.trades || []} /></td>
                        <td><AssignBadge status={row.status} /></td>
                        <td className="sa-actions-cell">
                            <button className="act-btn act-view" onClick={() => onView(row)}>
                                <IconEye size={12} stroke={1.5} /> View
                            </button>
                            {row.status === 'active' && (
                                <button className="act-btn act-retire" onClick={() => onArchive(row)}>
                                    <IconArchive size={12} stroke={1.5} /> Archive
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

// ── Site-view table ───────────────────────────────────────────────────────────

function SiteViewTable({ rows, onArchive, onView }) {
    // Group by site id
    const grouped = useMemo(() => {
        const map = new Map()
        rows.forEach((r) => {
            const siteId = r.site?._id ?? 'unknown'
            if (!map.has(siteId)) map.set(siteId, { site: r.site, assignments: [] })
            map.get(siteId).assignments.push(r)
        })
        return [...map.values()]
    }, [rows])

    if (grouped.length === 0) {
        return <p className="sm-empty sa-empty-padded">No assignments found.</p>
    }

    return (
        <table className="sa-table">
            <thead>
                <tr>
                    <th className="sa-col-site-hd">Site</th>
                    <th className="sa-col-swms-hd">SWMS</th>
                    <th className="sa-col-trades">Trades</th>
                    <th className="sa-col-status">Status</th>
                    <th className="sa-col-actions-lg"></th>
                </tr>
            </thead>
            <tbody>
                {grouped.map(({ site, assignments }) => {
                    const isComplete = site?.status === 'complete'
                    const rowClass = isComplete ? 'sa-site-row-complete' : 'sa-site-row-active'
                    return assignments.map((r, idx) => (
                        <tr key={r._id} className={`${rowClass}${r.status === 'archived' ? ' sa-row-archived' : ''}`}>
                            {idx === 0 && (
                                <td rowSpan={assignments.length} className="sa-cell-primary sa-cell-top">
                                    <div>{site?.fullAddress || site?.address || '—'}</div>
                                    {isComplete && <span className="sa-site-status-badge">Completed</span>}
                                </td>
                            )}
                            <td>{r.swmsStore?.title ?? '—'}</td>
                            <td><TradePills trades={r.trades || []} /></td>
                            <td><AssignBadge status={r.status} /></td>
                            <td className="sa-actions-cell">
                                <button className="act-btn act-view" onClick={() => onView(r)}>
                                    <IconEye size={12} stroke={1.5} /> View
                                </button>
                                {r.status === 'active' && (
                                    <button className="act-btn act-retire" onClick={() => onArchive(r)}>
                                        <IconArchive size={12} stroke={1.5} /> Archive
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))
                })}
            </tbody>
        </table>
    )
}

// ── Main SwmsAssignment component ─────────────────────────────────────────────

export default function SwmsAssignment() {
    const { companyId } = useParams()
    const queryClient = useQueryClient()

    const [view, setView] = useState('swms') // 'swms' | 'site'
    const [statusFilter, setStatusFilter] = useState('active')
    const [tradeFilter, setTradeFilter] = useState(null)
    const [search, setSearch] = useState('')
    const [assignModalOpen, setAssignModalOpen] = useState(false)
    const [confirm, setConfirm] = useState(null)
    const [viewer, setViewer] = useState(null)
    const [toast, setToast] = useState({ show: false, message: '', isError: false })
    const toastTimer = useRef(null)

    const showToast = (message, isError = false) => {
        clearTimeout(toastTimer.current)
        setToast({ show: true, message, isError })
        toastTimer.current = setTimeout(() => setToast({ show: false, message: '', isError: false }), 4000)
    }

    // ── Queries ───────────────────────────────────────────────────────────────
    const { data: assignments = [], isLoading: loadingAssignments } = useQuery(
        ['swmsAssignments', companyId],
        () => getSwmsAssignments()
    )
    const { data: allStore = [], isLoading: loadingStore } = useQuery(
        ['swmsStore', companyId],
        () => getSwmsStore()
    )
    const { data: sites = [], isLoading: loadingSites } = useQuery(
        ['projectSites', companyId],
        () => getProjectSites(companyId)
    )

    const publishedSwms = useMemo(() => allStore.filter((s) => s.status === 'published'), [allStore])
    const activeSites = useMemo(() => sites.filter((s) => s.status !== 'complete'), [sites])

    // ── Filtered rows ─────────────────────────────────────────────────────────
    const filteredRows = useMemo(() => {
        let rows = assignments
        if (statusFilter) rows = rows.filter((r) => r.status === statusFilter)
        if (tradeFilter) rows = rows.filter((r) => r.trades?.includes(tradeFilter))
        if (search.trim()) {
            const q = search.toLowerCase()
            rows = rows.filter(
                (r) =>
                    r.swmsStore?.title?.toLowerCase().includes(q) ||
                    r.site?.fullAddress?.toLowerCase().includes(q) ||
                    r.site?.address?.toLowerCase().includes(q) ||
                    r.trades?.some((t) => tradeLabel(t).toLowerCase().includes(q))
            )
        }
        return rows
    }, [assignments, statusFilter, tradeFilter, search])

    const allTrades = useMemo(() => {
        const set = new Set(assignments.flatMap((r) => r.trades || []))
        return [...set]
    }, [assignments])

    // ── Mutations ─────────────────────────────────────────────────────────────
    const invalidate = () => queryClient.invalidateQueries(['swmsAssignments', companyId])

    const createMutation = useMutation(createSwmsAssignment, {
        onSuccess: () => {
            invalidate()
            setAssignModalOpen(false)
            showToast('Assignment created')
        },
        onError: (e) => showToast(e.message || 'Failed to create assignment', true),
    })

    const archiveMutation = useMutation(({ id }) => archiveSwmsAssignment(id), {
        onSuccess: () => {
            invalidate()
            setConfirm(null)
            showToast('Assignment archived')
        },
        onError: (e) => showToast(e.message || 'Failed to archive assignment', true),
    })

    const handleArchiveConfirm = () => {
        if (!confirm) return
        archiveMutation.mutate({ id: confirm.id })
    }

    const openArchiveConfirm = (row) => {
        setConfirm({
            type: 'archive',
            id: row._id,
            title: 'Archive Assignment',
            label: row.swmsStore?.title,
            message: 'Archiving will remove this assignment from active use. Blob files are retained.',
            warningText: 'This action is irreversible. The assignment cannot be re-activated.',
        })
    }

    if (loadingAssignments || loadingStore || loadingSites) {
        return (
            <div className="sa-page sa-page-loading">
                <p className="sa-loading-text">Loading…</p>
            </div>
        )
    }

    return (
        <div className="sa-page">
            {/* Hero button */}
            <div className="sa-hero">
                <button className="sa-btn-hero" onClick={() => setAssignModalOpen(true)}>
                    <IconPlus size={16} stroke={2} />
                    Assign SWMS to a site
                </button>
            </div>

            {/* Filter bar */}
            <div className="sa-filter-bar">
                <div className="sa-filter-row">
                    <div className="sa-search-wrap">
                        <IconSearch size={14} className="sa-search-icon" />
                        <input
                            placeholder="Search by SWMS title, site or trade…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && (
                            <button className="sa-search-clear" onClick={() => setSearch('')}>
                                <IconX size={12} stroke={2} />
                            </button>
                        )}
                    </div>

                    {/* Status toggle */}
                    <div className="sa-status-pills">
                        <button
                            id="afs-active"
                            className={`sa-status-btn${statusFilter === 'active' ? ' on' : ''}`}
                            onClick={() => setStatusFilter('active')}
                        >
                            Active
                        </button>
                        <button
                            id="afs-archived"
                            className={`sa-status-btn${statusFilter === 'archived' ? ' on' : ''}`}
                            onClick={() => setStatusFilter('archived')}
                        >
                            Archived
                        </button>
                        <button
                            className={`sa-status-btn${!statusFilter ? ' on' : ''}`}
                            onClick={() => setStatusFilter(null)}
                        >
                            All
                        </button>
                    </div>
                </div>

                {/* Row 2: trade chips */}
                <div className="sa-filter-chips-row">
                    <div className="sa-filter-chips">
                        <button
                            className={`sa-chip${!tradeFilter ? ' on' : ''}`}
                            onClick={() => setTradeFilter(null)}
                        >
                            All trades
                        </button>
                        {allTrades.map((t) => (
                            <button
                                key={t}
                                className={`sa-chip${tradeFilter === t ? ' on' : ''}`}
                                onClick={() => setTradeFilter(t)}
                            >
                                {tradeLabel(t)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* View toggle row */}
            <div className="sa-view-row">
                <span className="sa-result-count">{filteredRows.length} assignment{filteredRows.length !== 1 ? 's' : ''}</span>
                <div className="sa-view-toggle">
                    <button
                        className={`sa-view-btn${view === 'swms' ? ' active' : ''}`}
                        onClick={() => setView('swms')}
                    >
                        <IconLayoutList size={13} stroke={1.5} /> SWMS view
                    </button>
                    <button
                        className={`sa-view-btn${view === 'site' ? ' active' : ''}`}
                        onClick={() => setView('site')}
                    >
                        <IconBuildingSkyscraper size={13} stroke={1.5} /> Site view
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="sa-table-wrap">
                {view === 'swms' ? (
                    <SwmsViewTable rows={filteredRows} onArchive={openArchiveConfirm} onView={setViewer} />
                ) : (
                    <SiteViewTable rows={filteredRows} onArchive={openArchiveConfirm} onView={setViewer} />
                )}
            </div>

            {/* Assign modal */}
            <AssignModal
                open={assignModalOpen}
                swmsList={publishedSwms}
                sites={activeSites}
                onClose={() => setAssignModalOpen(false)}
                onSubmit={(data) => createMutation.mutate(data)}
                isSubmitting={createMutation.isLoading}
            />

            {/* Archive confirm */}
            <ConfirmModal confirm={confirm} onConfirm={handleArchiveConfirm} onCancel={() => setConfirm(null)} />

            {/* SWMS viewer */}
            <ViewerOverlay record={viewer} onClose={() => setViewer(null)} />

            {/* Toast */}
            <Toast message={toast.message} show={toast.show} isError={toast.isError} />
        </div>
    )
}
